import { I18nPagination } from '@celebration/react/ClbPagination/ClbPagination.types';
import LoadsConstants from '../constants';
import ICarrierLoadCardInfo from '../interfaces/ICarrierLoadCardInfo';
import i18next from '../../../../lib/i18n';
import { StatusTagProps } from '../../../../common/components/load-card/load-card-header/ILoadCardHeader';
import LoadStatus from '../../../../common/enums/loads/load-status.enum';
import LoadSuspensionReason from '../enums/load-suspension-reason.enum';
import { getUTCDate } from '../../../../utils/date-utils';
import ILoadListData from '../../../../common/interfaces/loads/ILoadListData';
import ILoadRoute from '../../../../common/interfaces/loads/ILoadRoute';
import ILoadShipmentInfo from '../../../../common/interfaces/loads/ILoadShipmentInfo';
import ILoadVehicleInfo from '../../../../common/interfaces/loads/ILoadVehicleInfo';
import ILoadRate from '../../../../common/interfaces/loads/ILoadRate';
import ILoadCardInfo from '../../../../common/interfaces/loads/ILoadCardInfo';

const loadStatusMap: Record<number, string> = {
    [LoadStatus.Planned]: 'Planejada',
    [LoadStatus.Ready]: 'Pronta',
    [LoadStatus.Cancelled]: 'Cancelada',
    [LoadStatus.Expired]: 'Expirada',
    [LoadStatus.Confirmed]: 'Confirmada',
    [LoadStatus.Suspended]: 'Suspensa',
    [LoadStatus.WaitingAcceptance]: 'AguardandoAceitacao',
    [LoadStatus.WaitingConfirmation]: 'AguardandoConfirmacao',
    [LoadStatus.CancelledTms]: 'CanceladaTms',
    [LoadStatus.ConfirmedTms]: 'ConfirmadaTms',
    [LoadStatus.NoRate]: 'NoRate',
};

export const suspensionReasonMap: Record<number, string> = {
    [LoadSuspensionReason.Alocacao_Frota_Fixa]:
        'MotivoSuspensaoAlocacaoFrotaFixa',
    [LoadSuspensionReason.Reoferta_Carga]: 'MotivoSuspensaoReofertaCarga',
    [LoadSuspensionReason.Problema_Com_Transpotadora_Confirmada]:
        'MotivoSuspensaoProblemaComTranspotadoraConfirmada',
    [LoadSuspensionReason.Informacoes_Incorretas]:
        'MotivoSuspensaoInformacoesIncorretas',
    [LoadSuspensionReason.Erro_Desconhecido]: 'MotivoSuspensaoErroDesconhecido',
    [LoadSuspensionReason.Outro]: 'MotivoSuspensaoOutro',
};

export function getLoadTagStatusType(
    loadStatus: LoadStatus,
    hasRates: boolean,
): StatusTagProps {
    switch (loadStatus) {
        case LoadStatus.Ready:
            return {
                type: 'neutral',
                id: 'load-tag-em-oferta',
                dataTestId: 'load-tag-em-oferta',
                label: i18next.t('Pronta'),
            };
        case LoadStatus.Cancelled:
            return {
                type: 'negative',
                id: 'load-tag-cancelada',
                dataTestId: 'load-tag-cancelada',
                label: i18next.t('Cancelada'),
            };
        case LoadStatus.Expired:
            return {
                type: 'warning',
                id: 'load-tag-expirada',
                dataTestId: 'load-tag-expirada',
                label: i18next.t('Expirada'),
            };
        case LoadStatus.Confirmed:
            return {
                type: 'positive',
                id: 'load-tag-confirmada',
                dataTestId: 'load-tag-confirmada',
                label: i18next.t('Confirmada'),
            };
        case LoadStatus.CancelledTms:
            return {
                type: 'negative',
                id: 'load-tag-cancelada-tms',
                dataTestId: 'load-tag-cancelada-tms',
                label: i18next.t('CanceladaTms'),
            };
        case LoadStatus.ConfirmedTms:
            return {
                type: 'positive',
                id: 'load-tag-confirmada-tms',
                dataTestId: 'load-tag-confirmada-tms',
                label: i18next.t('ConfirmadaTms'),
            };
        case LoadStatus.WaitingAcceptance:
            return {
                type: 'neutral',
                id: 'load-tag-aguard-aceitacao',
                dataTestId: 'load-tag-aguard-aceitacao',
                label: i18next.t('AguardandoAceitacao'),
            };
        case LoadStatus.WaitingConfirmation:
            return {
                type: 'neutral',
                id: 'load-tag-aguard-confirmacao',
                dataTestId: 'load-tag-aguard-confirmacao',
                label: i18next.t('AguardandoConfirmacao'),
            };
        case LoadStatus.Suspended:
            return {
                type: 'warning',
                id: 'load-tag-suspensa',
                dataTestId: 'load-tag-suspensa',
                label: i18next.t('Suspensa'),
            };
        case LoadStatus.Planned:
            return {
                type: hasRates ? 'neutral' : 'negative',
                id: 'load-tag-planejada',
                dataTestId: 'load-tag-planejada',
                label: i18next.t('Planejada'),
            };
        default:
            return {
                type: 'neutral',
                id: 'load-tag-default',
                dataTestId: 'load-tag-default',
                label: LoadsConstants.NO_INFO_TEXT,
            };
    }
}

export const statusMap = (status: string) => {
    switch (status) {
        case 'Ready':
            return LoadStatus.Ready;
        case 'Cancelled':
            return LoadStatus.Cancelled;
        case 'Expired':
            return LoadStatus.Expired;
        case 'Confirmed':
            return LoadStatus.Confirmed;
        case 'CancelledTms':
            return LoadStatus.CancelledTms;
        case 'ConfirmedTms':
            return LoadStatus.ConfirmedTms;
        case 'WaitingAcceptance':
            return LoadStatus.WaitingAcceptance;
        case 'WaitingConfirmation':
            return LoadStatus.WaitingConfirmation;
        case 'Suspended':
            return LoadStatus.Suspended;
        case 'Planned':
            return LoadStatus.Planned;
        default:
            return '';
    }
};

export function getSelectOptionsEnumLoadStatus(isPool?: boolean) {
    const options: any[] = [];

    let enumStrings = Object.keys(LoadStatus).filter(
        enumKey => Number.isNaN(Number(enumKey)) && enumKey !== 'NoRate',
    );

    if (isPool) {
        enumStrings = ['WaitingConfirmation', 'NoRate'];
    }

    enumStrings.forEach((enumKey: string) => {
        options.push({
            value: enumKey,
            label: loadStatusMap[
                LoadStatus[enumKey as keyof typeof LoadStatus]
            ],
        });
    });

    return options;
}

export function getSelectOptionsEnumLoadSuspensionReason() {
    const enumOptions: any[] = [];

    const enumStrings = Object.keys(LoadSuspensionReason).filter(key =>
        Number.isNaN(Number(key)),
    );

    enumStrings.forEach((key: string) => {
        enumOptions.push({
            value: key,
            label: suspensionReasonMap[
                LoadSuspensionReason[key as keyof typeof LoadSuspensionReason]
            ],
        });
    });

    return enumOptions;
}

const getLoadChannel = (channel?: string): string => {
    return channel === '' || channel === undefined
        ? LoadsConstants.NO_INFO_TEXT
        : channel;
};

export const getLocalDate = (date?: Date): Date | undefined => {
    if (date === undefined) {
        return undefined;
    }

    return new Date(date);
};

export const getFullTime = (date?: Date): string | undefined => {
    const convertedDate = getLocalDate(date);

    return `${convertedDate
        ?.getHours()
        .toString()
        .padStart(2, '0')}:${convertedDate
        ?.getMinutes()
        .toString()
        .padStart(2, '0')}:${convertedDate
        ?.getSeconds()
        .toString()
        .padStart(2, '0')}`;
};

const getLoadRoutDetails = (load: ILoadListData): ILoadRoute => {
    return {
        collectionDate:
            getUTCDate(load.dates?.collectionDate) ??
            LoadsConstants.NO_INFO_TEXT,
        destinationCode:
            load.route?.destination?.code ?? LoadsConstants.NO_INFO_TEXT,
        destinationDescription:
            load.route?.destination?.description ?? LoadsConstants.NO_INFO_TEXT,
        originCode: load.route?.source?.code ?? LoadsConstants.NO_INFO_TEXT,
        originDescription:
            load.route?.source?.description ?? LoadsConstants.NO_INFO_TEXT,
        source: load.route?.source,
        destination: load.route?.destination,
    } as ILoadRoute;
};

const getLoadShipmentInfo = (load: ILoadListData): ILoadShipmentInfo => {
    return {
        material: load.shipment.material.name ?? LoadsConstants.NO_INFO_TEXT,
        packing: load.shipment.packing.name ?? LoadsConstants.NO_INFO_TEXT,
        weight: load.shipment.weight ?? 0,
        weightUnitMeasure:
            load.shipment.weightUnitMeasure ?? LoadsConstants.NO_INFO_TEXT,
        currency: load.shipment.currency,
        skids: load.shipment.skids
            ? parseInt(load.shipment.skids, 10)
            : undefined,
        freightOrder: load.shipment.freightOrder,
    } as ILoadShipmentInfo;
};

const getLoadVehicleInfo = (load: ILoadListData): ILoadVehicleInfo => {
    return {
        id: load.vehicle?.id ?? LoadsConstants.NO_INFO_TEXT,
        name: load.vehicle?.name ?? LoadsConstants.NO_INFO_TEXT,
        description: load.vehicle?.description ?? LoadsConstants.NO_INFO_TEXT,
    } as ILoadVehicleInfo;
};

const getLoadWinnerInfo = (load: ILoadListData): ICarrierLoadCardInfo => {
    return {
        carrierCode:
            load.winnerInfo?.carrierCode ?? LoadsConstants.NO_INFO_TEXT,
        carrierName:
            load.winnerInfo?.carrierName ?? LoadsConstants.NO_INFO_TEXT,
        vehicleDescription: load.winnerInfo?.vehicleDescription,
    } as ICarrierLoadCardInfo;
};

const getLoadRates = (load: ILoadListData): ILoadRate[] => {
    return load.rates.map(
        rate =>
            ({
                ...rate,
                sulahubId: rate.sulaHubId,
                corporateName:
                    rate.corporateName ?? i18next.t('CarrierWithoutRegister'),
            }) as ILoadRate,
    );
};

export function fillLoadInfo(load: ILoadListData): ILoadCardInfo {
    return {
        idLoad: load.loadId,
        channel: getLoadChannel(load.channel),
        prioritized: load.isPrioritized,
        routeDetails: getLoadRoutDetails(load),
        winnerInfo: getLoadWinnerInfo(load),
        tagStatus: statusMap(load.status),
        statusReason: load.statusReason ?? LoadsConstants.NO_INFO_TEXT,
        shipmentNumber: load.shipmentNumber,
        sapOrderNumber: load.sapOrderNumber,
        vehicle: getLoadVehicleInfo(load),
        shipment: getLoadShipmentInfo(load),
        checked: load.checked ?? false,
        planningDate: load?.dates?.planningDate ?? LoadsConstants.NO_INFO_TEXT,
        type: load.type,
        rates: getLoadRates(load),
        isPool: load.isPool,
        isPocFreightAuction: load.isPocFreightAuction,
        isReturnable: load.isReturnable,
    } as ILoadCardInfo;
}

export function getDateFromStringDDMMYYYY(date: string): Date {
    if (!date) {
        return new Date(0);
    }

    const [DD, MM, YYYY] = date.split(/[/\\]/).map(x => Number.parseInt(x, 10));

    return new Date(YYYY, MM - 1, DD);
}

export function parseStringToNumber(value: string): number {
    if (value === undefined) {
        return 0;
    }

    return Number.parseFloat(value) || 0;
}

export function getI18nForTablePagination(
    t: (key: string) => string,
): I18nPagination {
    return {
        ariaLabelCurrentPage: t('PaginaAtual'),
        ariaLabelFirstPage: t('PrimeiraPagina'),
        ariaLabelLastPage: t('UltimaPagina'),
        ariaLabelMoreEndPage: t('MostrarMaisPaginasFinal'),
        ariaLabelMoreStartPage: t('MostrarMaisPaginasInicio'),
        ariaLabelNextPage: t('ProximaPagina'),
        ariaLabelNumberItemsPage: t('QuantidadeItensPagina'),
        ariaLabelPageButton: t('SelecionarPagina'),
        ariaLabelPreviousPage: t('PaginaAnterior'),
        linePage: t('LinhasPorPagina'),
        totals: t('ResultadosTotais'),
        noResults: t('NenhumResultado'),
    } as I18nPagination;
}

export function isPastLoad(date?: Date): boolean {
    if (date === undefined) {
        return false;
    }

    return (
        date.setHours(0, 0, 0, 0) < new Date(Date.now()).setHours(0, 0, 0, 0)
    );
}
